// ============================================================================
// BP-13-02: Moderation Queue Component
// BrightPath Platform - Admin Frontend
// ============================================================================

import React from 'react';
import { cn } from '@/core/lib/utils';
import type { ModerationQueueItem, ModerationItemType, ModerationStatus } from '@/types/admin';
import { useApproveItem, useRejectItem, useEscalateItem } from '@/features/admin/hooks/useModeration';

// =============================================================================
// Types
// =============================================================================

interface ModerationQueueProps {
  items: ModerationQueueItem[];
  isLoading?: boolean;
  onItemClick?: (itemId: string) => void;
}

// =============================================================================
// Type Badge
// =============================================================================

function TypeBadge({ type }: { type: ModerationItemType }) {
  const typeConfig: Record<ModerationItemType, { label: string; className: string }> = {
    forum_post: { label: 'Forum Post', className: 'bg-blue-100 text-blue-700' },
    forum_reply: { label: 'Reply', className: 'bg-blue-50 text-blue-600' },
    product_review: { label: 'Product Review', className: 'bg-terracotta/10 text-terracotta' },
    vendor_review: { label: 'Vendor Review', className: 'bg-terracotta/10 text-terracotta' },
    resource_comment: { label: 'Comment', className: 'bg-purple-100 text-purple-700' },
    user_profile: { label: 'Profile', className: 'bg-sage/10 text-sage-dark' },
    vendor_profile: { label: 'Vendor', className: 'bg-navy/10 text-navy' },
    product_listing: { label: 'Product', className: 'bg-amber-100 text-amber-700' },
  };

  const config = typeConfig[type];

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        config.className
      )}
    >
      {config.label}
    </span>
  );
}

// =============================================================================
// Priority Indicator
// =============================================================================

function PriorityIndicator({ priority }: { priority: 'low' | 'medium' | 'high' | 'urgent' }) {
  const priorityConfig = {
    low: { color: 'bg-gray-400', label: 'Low' },
    medium: { color: 'bg-amber-400', label: 'Medium' },
    high: { color: 'bg-orange-500', label: 'High' },
    urgent: { color: 'bg-red-500', label: 'Urgent' },
  };

  const config = priorityConfig[priority];

  return (
    <div className="flex items-center gap-1.5" title={`${config.label} priority`}>
      <span className={cn('h-2 w-2 rounded-full', config.color)} />
      <span className="text-xs text-warm-gray">{config.label}</span>
    </div>
  );
}

// =============================================================================
// Queue Item Card
// =============================================================================

interface QueueItemCardProps {
  item: ModerationQueueItem;
  onItemClick?: (itemId: string) => void;
}

function QueueItemCard({ item, onItemClick }: QueueItemCardProps) {
  const approveItem = useApproveItem();
  const rejectItem = useRejectItem();
  const escalateItem = useEscalateItem();

  const handleApprove = (e: React.MouseEvent) => {
    e.stopPropagation();
    approveItem.mutate(item.id);
  };

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation();
    rejectItem.mutate(item.id);
  };

  const handleEscalate = (e: React.MouseEvent) => {
    e.stopPropagation();
    escalateItem.mutate(item.id);
  };

  const isProcessing =
    approveItem.isPending || rejectItem.isPending || escalateItem.isPending;

  return (
    <div
      onClick={() => onItemClick?.(item.id)}
      className={cn(
        'group cursor-pointer rounded-xl border border-gray-200 bg-white p-4 transition-all duration-200',
        'hover:border-sage/30 hover:shadow-md',
        item.priority === 'urgent' && 'border-l-4 border-l-red-500',
        item.priority === 'high' && 'border-l-4 border-l-orange-500'
      )}
    >
      {/* Header */}
      <div className="mb-3 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <TypeBadge type={item.itemType} />
          {item.flagCount > 1 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
              <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z"
                  clipRule="evenodd"
                />
              </svg>
              {item.flagCount} flags
            </span>
          )}
        </div>
        <PriorityIndicator priority={item.priority} />
      </div>

      {/* Content Preview */}
      <div className="mb-3">
        <p className="text-sm font-medium text-navy">{item.authorName}</p>
        <p className="mt-1 line-clamp-2 text-sm text-warm-gray">{item.contentPreview}</p>
      </div>

      {/* Flag Reason */}
      {item.flagReason && (
        <p className="mb-3 text-xs text-red-600">
          Flagged for: <span className="font-medium">{item.flagReason.replace('_', ' ')}</span>
        </p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-gray-100 pt-3">
        <span className="text-xs text-warm-gray">{item.waitTime}</span>

        {/* Quick Actions */}
        <div className="flex items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            onClick={handleApprove}
            disabled={isProcessing}
            className="rounded-lg bg-emerald-100 px-3 py-1.5 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-200 disabled:opacity-50"
          >
            {approveItem.isPending ? '...' : 'Approve'}
          </button>
          <button
            onClick={handleReject}
            disabled={isProcessing}
            className="rounded-lg bg-red-100 px-3 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-200 disabled:opacity-50"
          >
            {rejectItem.isPending ? '...' : 'Reject'}
          </button>
          <button
            onClick={handleEscalate}
            disabled={isProcessing}
            className="rounded-lg bg-amber-100 px-3 py-1.5 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-200 disabled:opacity-50"
          >
            {escalateItem.isPending ? '...' : 'Escalate'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Loading Skeleton
// =============================================================================

function QueueSkeleton() {
  return (
    <div className="space-y-4">
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className="animate-pulse rounded-xl border border-gray-200 bg-white p-4"
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="h-5 w-20 rounded-full bg-gray-200" />
            <div className="h-4 w-16 rounded bg-gray-100" />
          </div>
          <div className="space-y-2">
            <div className="h-4 w-1/3 rounded bg-gray-200" />
            <div className="h-4 w-full rounded bg-gray-100" />
            <div className="h-4 w-2/3 rounded bg-gray-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Empty State
// =============================================================================

function EmptyQueue() {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white py-12">
      <div className="mb-4 rounded-full bg-emerald-100 p-3">
        <svg
          className="h-8 w-8 text-emerald-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <h3 className="mb-1 text-lg font-semibold text-navy">All caught up!</h3>
      <p className="text-warm-gray">No items currently need moderation.</p>
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

export function ModerationQueue({ items, isLoading, onItemClick }: ModerationQueueProps) {
  if (isLoading) {
    return <QueueSkeleton />;
  }

  if (items.length === 0) {
    return <EmptyQueue />;
  }

  return (
    <div className="space-y-4">
      {items.map((item) => (
        <QueueItemCard key={item.id} item={item} onItemClick={onItemClick} />
      ))}
    </div>
  );
}

export default ModerationQueue;
